/**
 * HelixPanel.tsx — W66.X6 REBUILD: Command View — Opportunity Health / DNA Helix
 *
 * Renders a live DNA helix orb strip per Bryan K. Short canonical money-shot spec
 * (locked 2026-05-30 / confirmed 2026-06-02).
 *
 * Pattern reused from client/src/components/methodology/HelixHorizon.tsx — same
 * alternating orb strip, same spine, same health legend. The difference from
 * HelixHorizon is that orb colors are driven by LIVE per-CP scores pulled from
 * Supabase account_answers (same hydration logic as OperationalHelix), not demo
 * data.
 *
 * Why not embed OperationalHelix directly:
 *   OperationalHelix is a full 3-column editor surface (legend + 3D helix + Q&A
 *   panel) with minHeight 500px. Inside the Command View panel it is crushed on
 *   iPad and renders invisible. Bryan confirmed on 2026-06-02 he wants the SAME
 *   visual as the methodology H4 horizon — the DNA orb strip — not the full editor.
 *
 * W45.3: wired to SelectedCpContext. Clicking any CP orb calls setSelectedCp.
 *
 * Brand: No exclamation points. No emojis. M-PACT hyphenated.
 * "Checkpoint" not "Gate". AE name is Demo AE 1 — never Geoff.
 * 5-color palette: #6B7280 gray, #DC2626 red, #EA580C orange, #EAB308 yellow, #16A34A green.
 */

import React, { useEffect, useState, useCallback } from "react";
import { useSelectedCp } from "@/hooks/useSelectedCp";
import { HELIX_CHECKPOINTS } from "@/lib/helix-checkpoints";
import { getHealthColor, HEALTH_PALETTE } from "@shared/schema";
import { supabase } from "@/lib/session";

// ─── Types ────────────────────────────────────────────────────────────────────

interface CpOrb {
  cpNumber: number;
  name: string;
  color: string;      // hex from 5-color palette
  pct: number;        // 0-100 score or -1 (not answered)
  label: string;      // "Strong" | "Qualified" | "Developing" | "Early" | "—"
}

// ─── Score label from pct ────────────────────────────────────────────────────

function scoreBucket(pct: number): string {
  if (pct < 0) return "—";
  if (pct >= 80) return "Strong";
  if (pct >= 60) return "Qualified";
  if (pct >= 40) return "Developing";
  if (pct > 0)   return "Early";
  return "—";
}

// ─── Supabase hydration ───────────────────────────────────────────────────────
// Mirrors the OperationalHelix DB hydration — pull account_answers for this
// account, compute per-CP pct from selected_option (graded 5-point scale),
// return an array of CpOrb objects.

interface AnswerRow {
  question_key: string;
  selected_option: string | null;
}

// Simple per-CP pct: average of (option_value / 4) across answered questions.
// "0".."4" graded scale — same math as computeCpScore in OperationalHelix.
function computeOrbColor(cpNumber: number, rows: AnswerRow[]): { pct: number; color: string } {
  const cpRows = rows.filter((r) => r.question_key.startsWith(`q${cpNumber}.`) || r.question_key.startsWith(`Q${cpNumber}.`));
  if (cpRows.length === 0) return { pct: -1, color: HEALTH_PALETTE.gray };

  let sumPoints = 0;
  let sumMax = 0;
  for (const r of cpRows) {
    const opt = r.selected_option;
    if (opt === null || opt === undefined) continue;
    const n = parseFloat(opt);
    if (!isNaN(n)) {
      sumPoints += Math.max(0, Math.min(4, n));
      sumMax += 4;
    }
  }

  if (sumMax === 0) return { pct: -1, color: HEALTH_PALETTE.gray };
  const pct = Math.round((sumPoints / sumMax) * 100);
  return { pct, color: getHealthColor(pct) };
}

// ─── Description card ────────────────────────────────────────────────────────

function HelixDescriptionCard() {
  return (
    <div
      style={{
        background: "#FFFFFF",
        border: "1px solid #E5E7EB",
        borderRadius: 8,
        padding: 12,
        flexShrink: 0,
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", marginBottom: 4 }}>
        Opportunity DNA — Helix Health Model
      </div>
      <div style={{ fontSize: 11, color: "#6B7280", lineHeight: 1.55 }}>
        DNA diagnostic. Each checkpoint maps to a health orb on the double-helix
        spine. Orb color reflects answer strength — Strong, Qualified, Developing,
        Early. Tap a CP orb to focus that checkpoint across all three panels.
      </div>
    </div>
  );
}

// ─── Live helix orb strip (mirrors HelixHorizon.HelixStrip but with live data) ─

interface LiveHelixStripProps {
  orbs: CpOrb[];
  selectedCp: number | null;
  onOrbClick: (cp: number) => void;
}

function LiveHelixStrip({ orbs, selectedCp, onOrbClick }: LiveHelixStripProps) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 6,
        position: "relative",
        padding: "8px 0",
        flex: 1,
        overflowY: "auto",
        minHeight: 0,
      }}
    >
      {/* Spine */}
      <div
        style={{
          position: "absolute",
          left: "50%",
          top: 0,
          bottom: 0,
          width: 2,
          background: "rgba(148,163,184,0.15)",
          transform: "translateX(-50%)",
          zIndex: 0,
        }}
      />

      {orbs.map((orb, idx) => {
        const side = idx % 2 === 0 ? "left" : "right";
        const isSelected = selectedCp === orb.cpNumber;
        const orbColor = orb.color;
        const orbBorder = isSelected
          ? `3px solid #EAB308`
          : `2px solid rgba(255,255,255,0.15)`;
        const orbShadow = isSelected
          ? `0 0 12px #EAB30899, 0 0 6px ${orbColor}66`
          : `0 0 8px ${orbColor}66`;

        return (
          <button
            key={orb.cpNumber}
            type="button"
            onClick={() => onOrbClick(orb.cpNumber)}
            aria-label={`Checkpoint ${orb.cpNumber} — ${orb.name} — ${orb.label}`}
            style={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              gap: 8,
              flexDirection: side === "left" ? "row" : "row-reverse",
              position: "relative",
              zIndex: 1,
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: "1px 0",
            }}
          >
            {/* CP number label */}
            <div
              style={{
                flex: 1,
                textAlign: side === "left" ? "right" : "left",
                fontSize: 10,
                color: isSelected ? "#EAB308" : "rgba(255,255,255,0.5)",
                whiteSpace: "nowrap",
                fontWeight: isSelected ? 700 : 500,
                transition: "color 0.2s",
              }}
            >
              CP{orb.cpNumber}
            </div>

            {/* Orb */}
            <div
              style={{
                width: 22,
                height: 22,
                borderRadius: "50%",
                background: orbColor,
                border: orbBorder,
                boxShadow: orbShadow,
                flexShrink: 0,
                transition: "box-shadow 0.2s, border 0.2s",
              }}
            />

            {/* Name label */}
            <div
              style={{
                flex: 1,
                textAlign: side === "right" ? "right" : "left",
                fontSize: 10,
                color: isSelected ? "rgba(234,179,8,0.85)" : "rgba(255,255,255,0.45)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: 90,
                transition: "color 0.2s",
              }}
            >
              {orb.name}
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ─── Health legend ────────────────────────────────────────────────────────────

function HelixLegend() {
  const items = [
    { color: HEALTH_PALETTE.green,  label: "Strong" },
    { color: HEALTH_PALETTE.yellow, label: "Qualified" },
    { color: HEALTH_PALETTE.orange, label: "Developing" },
    { color: HEALTH_PALETTE.red,    label: "Early" },
  ];
  return (
    <div
      style={{
        display: "flex",
        gap: 10,
        flexWrap: "wrap",
        paddingTop: 6,
        flexShrink: 0,
      }}
    >
      {items.map(({ color, label }) => (
        <div key={label} style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: color }} />
          <span style={{ fontSize: 10, color: "rgba(255,255,255,0.4)" }}>{label}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface HelixPanelProps {
  accountId: string;
  accountName?: string;
  industry?: string;
  compositeScore: number;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function HelixPanel({
  accountId,
  accountName,
  compositeScore,
}: HelixPanelProps) {
  const { selectedCp, setSelectedCp } = useSelectedCp();
  const [orbs, setOrbs] = useState<CpOrb[]>(() =>
    HELIX_CHECKPOINTS.map((cp) => ({
      cpNumber: cp.number,
      name: cp.name,
      color: HEALTH_PALETTE.gray,
      pct: -1,
      label: "—",
    }))
  );
  const [loading, setLoading] = useState(true);

  // Hydrate orb colors from Supabase account_answers
  useEffect(() => {
    const accountIdNum = parseInt(accountId, 10);
    if (isNaN(accountIdNum)) { setLoading(false); return; }
    let cancelled = false;

    supabase()
      .from("account_answers")
      .select("question_key, selected_option")
      .eq("account_id", accountIdNum)
      .then(({ data }) => {
        if (cancelled) return;
        const rows: AnswerRow[] = (data ?? []).map((r: { question_key: string; selected_option: string | null }) => ({
          question_key: r.question_key,
          selected_option: r.selected_option,
        }));
        const computed = HELIX_CHECKPOINTS.map((cp) => {
          const { pct, color } = computeOrbColor(cp.number, rows);
          return {
            cpNumber: cp.number,
            name: cp.name,
            color,
            pct,
            label: scoreBucket(pct),
          };
        });
        setOrbs(computed);
        setLoading(false);
      })
      .catch(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [accountId]);

  const handleOrbClick = useCallback((cp: number) => {
    setSelectedCp(selectedCp === cp ? null : cp);
  }, [selectedCp, setSelectedCp]);

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: 320,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ── Panel header ── */}
      <div
        style={{
          padding: "10px 14px 8px",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          flexShrink: 0,
        }}
      >
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "#6B7280",
          }}
        >
          Opportunity Health
        </div>
        {accountName && (
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "rgba(250,250,247,0.9)",
              marginTop: 2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {accountName}
          </div>
        )}
      </div>

      {/* ── Content area ── */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          padding: "8px 12px",
          gap: 8,
          background: "#0A0A0C",
        }}
      >
        {/* Description card */}
        <HelixDescriptionCard />

        {/* Loading state */}
        {loading && (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", letterSpacing: "0.06em" }}>
              Loading health data…
            </span>
          </div>
        )}

        {/* Live orb strip */}
        {!loading && (
          <>
            <LiveHelixStrip
              orbs={orbs}
              selectedCp={selectedCp}
              onOrbClick={handleOrbClick}
            />
            <HelixLegend />
          </>
        )}

        {/* "Not scored yet" — secondary badge only, does not replace the strip */}
        {!loading && compositeScore === 0 && (
          <div
            style={{
              flexShrink: 0,
              textAlign: "center",
              fontSize: 10,
              fontWeight: 600,
              color: "#6B7280",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              padding: "2px 0 4px",
            }}
          >
            Not scored yet
          </div>
        )}
      </div>
    </div>
  );
}
